import CtaGroup from '@/features/microsite/components/CtaGroup';
import HeroPill from '@/features/microsite/components/HeroPill';
import MicrositeShell from '@/features/microsite/components/MicrositeShell';
import ProjectDescription, {HighlightNote} from '@/features/microsite/components/ProjectDescription';
import ProjectDescriptionRow from '@/features/microsite/components/ProjectDescriptionRow';
import {formatPublicDates, joinList, joinWithOr} from '@/features/microsite/utils/format';
import ProjectDescriptionTag from '@/components/ui/ProjectDescriptionTag';
import useProjectChannel from '@/hooks/useProjectChannel';
import usePublicProject from '@/hooks/usePublicProject';
import {TrophyIcon} from '@heroicons/react/24/outline';
import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

export default function ShareProjectPage() {
  const {t} = useTranslation();
  const {projectId} = useParams();
  const {project: fetched, isLoading, isError, refresh} = usePublicProject(projectId);

  // Joins translated commitment labels with "or" (see joinWithOr).
  /**
   * @param {Array<string>} values
   * @returns {string}
   */
  const joinCommitments = (values) => (
    joinWithOr((values || []).map((v) => t(`projects.microsite.commitment_options.${v}`, v)))
  );

  // Cross-tab edits arrive as a `project:update` broadcast. Any `fields` on the
  // message are applied optimistically for zero-latency title/image swaps; then
  // `refresh()` re-pulls the public projection (cache-busted) so every other
  // changed field — visibility, dates, tags, activity, … — updates live too.

  /**
   * @typedef {Object} LiveFields
   * @property {string} [title]
   * @property {string} [imageUrl]
   */

  /** @type {[LiveFields, React.Dispatch<React.SetStateAction<LiveFields>>]} */
  const [liveFields, setLiveFields] = useState({});
  const onMessage = useCallback((message) => {
    if (message?.type === 'project:update') {
      if (message.fields) {
        setLiveFields((prev) => ({...prev, ...message.fields}));
      }
      refresh();
    }
  }, [refresh]);
  useProjectChannel(projectId, onMessage);

  const project = fetched && {
    ...fetched,
    title: liveFields.title ?? fetched.title,
    image: liveFields.imageUrl
      ? {...fetched.image, imageUrl: liveFields.imageUrl}
      : fetched.image
  };

  const location = [project?.city, project?.state].filter(Boolean).join(', ');
  const dateLabel = formatPublicDates(project?.date);
  const studentAges = project?.student_ages?.length ? project.student_ages.join(', ') : null;
  const commitmentLabel = project?.commitment?.length ? joinCommitments(project.commitment) : null;

  const description = project && (
    <ProjectDescription
      title={project.title}
      summaryTitle={project.activity}
      description={t(`projects.microsite.activity_description.${project.activity}`)}
      completed={project.completed && <CtaGroup
        type="alt"
        heading={<div className="flex gap-2 items-center"><TrophyIcon
          className="bg-[#4E5A20] size-12 text-white rounded-full p-2 shrink-0"/>
          <div>{t('projects.microsite.completed_cta.heading')}</div>
        </div>}
        body={t('projects.microsite.completed_cta.body')}
        buttonLabel={t('projects.microsite.completed_cta.button_label')}
        primaryHref='/signup'
      />}
    >
      <ProjectDescriptionRow label={t('microsite.project.description.where_label')}>
        <span className="flex flex-wrap items-center gap-2">
          {location &&
            <span className="text-base leading-tight text-cs-gray-500">{location}</span>}
          {(project.participation_options || []).map((opt) => (
            <ProjectDescriptionTag key={opt} variant="orange">{opt}</ProjectDescriptionTag>
          ))}
        </span>
      </ProjectDescriptionRow>

      <ProjectDescriptionRow label={t('microsite.project.description.working_with_label')}>
        <span className="flex flex-wrap items-center gap-2">
          {studentAges && <ProjectDescriptionTag>{studentAges}</ProjectDescriptionTag>}
          {project.groupSize && <ProjectDescriptionTag>{project.groupSize}</ProjectDescriptionTag>}
        </span>
      </ProjectDescriptionRow>

      <ProjectDescriptionRow label={t('microsite.project.description.topic_areas_label')}>
        <p className="text-base leading-tight text-cs-gray-500 whitespace-pre-wrap">
          {joinList(project.subject_areas)}
        </p>
      </ProjectDescriptionRow>

      <ProjectDescriptionRow label={t('microsite.project.description.experience_areas_label')}>
        <div className="flex flex-col gap-3">
          <p className="text-base leading-tight text-cs-gray-500 whitespace-pre-wrap">
            {joinList(project.career_experience)}
          </p>
          <HighlightNote>
            {t('microsite.project.description.experience_note')}
          </HighlightNote>
        </div>
      </ProjectDescriptionRow>

      <ProjectDescriptionRow label={t('microsite.project.description.date_window_label')}>
        <ProjectDescriptionTag>{dateLabel}</ProjectDescriptionTag>
      </ProjectDescriptionRow>

      <ProjectDescriptionRow label={t('microsite.project.description.time_commitment_label')}>
        <span className="flex flex-wrap items-center gap-3">
          {commitmentLabel &&
            <span className="text-base leading-tight text-cs-gray-500">{commitmentLabel}</span>}
          {project.commitmentNotes && <HighlightNote>{project.commitmentNotes}</HighlightNote>}
          <HighlightNote>
            {t('microsite.project.description.schedule_note')}
          </HighlightNote>
        </span>
      </ProjectDescriptionRow>
    </ProjectDescription>
  );

  return (
    <MicrositeShell
      pageTitle={project?.title || t('microsite.project.title')}
      primaryHref={`/project/${projectId}`}
      isLoading={isLoading}
      isError={isError}
      notFoundMessage={t('microsite.project.not_found')}
      hasData={!!project}
      hero={{
        image: project?.image?.imageUrl,
        headingPrefix: t('microsite.project.hero.heading_prefix'),
        headingHighlight: t('microsite.project.hero.heading_highlight'),
        subtitlePrefix: t('microsite.project.hero.subtitle_prefix'),
        subtitleBold: t('microsite.project.hero.subtitle_bold'),
        pills: project && (
          <>
            {project.activity && <HeroPill>{project.activity}</HeroPill>}
            {(project.participation_options || []).map((opt) => (
              <HeroPill key={opt}>{opt}</HeroPill>
            ))}
            <HeroPill>{dateLabel}</HeroPill>
          </>
        )
      }}
      description={description}
      showCtas={!project?.completed}
      interestForm={{itemType: 'project', itemId: project?.id, itemName: project?.title}}
    />
  );
}
